import { Info } from "lucide-react"
import * as React from "react"

import { type DetectorProfileResolved } from "@/components/domain/detector-profile"
import { Skeleton } from "@/components/ui/alert"

import {
  masterLabelOf,
  printedLabelOf,
  type Report,
  type ReviewDocumentEntry,
} from "../report-data"

import { CoverageBlock } from "./coverage-block"

export interface SummaryViewProps {
  report: Report
  /** The review's document set. Undefined while it loads; the two lines it names wait for it. */
  documents: readonly ReviewDocumentEntry[] | undefined
  /** When the printed label was printed, as submitted. */
  printedOn: string | null
  /** The settings the review applied. Null when it recorded none. */
  detectorProfile?: DetectorProfileResolved | null
}

/**
 * The report read as prose: the summary, what was compared against what, and
 * what the review did and did not check.
 *
 * The facts are provenance a client needs to trust the report — which master,
 * which printed label — rather than a breakdown of our own severities, which
 * the other tabs already give.
 */
export function SummaryView({
  report,
  documents,
  printedOn,
  detectorProfile = null,
}: SummaryViewProps): JSX.Element {
  const notesId = React.useId()
  const docsLoaded = documents !== undefined
  const docs = documents ?? []
  const notes = report.master?.reasons ?? []

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-6">
      <section
        aria-labelledby="report-summary-heading"
        className="rounded-lg border border-border bg-surface p-6 shadow-e1"
      >
        <h2 id="report-summary-heading" className="text-eyebrow uppercase text-text-3">
          Summary
        </h2>
        {report.summaryParagraphs.length > 0 ? (
          <div className="mt-3 space-y-4 text-body text-text">
            {report.summaryParagraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-body text-text-3">No summary was written for this report.</p>
        )}

        <dl className="mt-6 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 [&>*:last-child:nth-child(odd)]:sm:col-span-2">
          <Fact label="Master" value={docsLoaded ? masterLabelOf(report.master, docs) : null} />
          <Fact label="Printed label" value={docsLoaded ? printedLabelOf(docs) : null} />
          {printedOn && <Fact label="Printed" value={printedOn} />}
          <Fact label="Divergences" value={String(report.divergenceTotal)} numeric />
        </dl>

        {/* Headed, not a bare list under the grid: it qualifies the Master line
            alone, and the note that matters says we may have compared against
            a different version than the one in force when this was printed. */}
        {notes.length > 0 && (
          <section
            aria-labelledby={notesId}
            className="mt-4 flex gap-3 rounded-lg bg-surface-sunken px-4 py-3"
          >
            <Info className="mt-0.5 size-4 shrink-0 text-text-3" aria-hidden="true" />
            <div className="min-w-0">
              <h3 id={notesId} className="text-body-sm font-semibold text-text">
                About this master
              </h3>
              <ul className="mt-1 space-y-1 text-body-sm text-text-2">
                {notes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </section>

      {/* Directly under the prose, because the prose is what over-promises:
          "no divergences requiring action" reads as a complete check, and this
          is what qualifies it. */}
      <CoverageBlock coverage={report.coverage} detectorProfile={detectorProfile} />
    </div>
  )
}

function Fact({
  label,
  value,
  numeric = false,
}: {
  label: string
  /** Null while the value is still loading. */
  value: string | null
  numeric?: boolean
}): JSX.Element {
  return (
    <div className="min-w-0 bg-surface px-4 py-3">
      <dt className="text-eyebrow uppercase text-text-3">{label}</dt>
      <dd
        className={
          numeric
            ? "mt-1 text-body font-semibold tabular-nums text-text"
            : "mt-1 break-words text-body-sm text-text"
        }
      >
        {value ?? <Skeleton className="h-4 w-40" />}
      </dd>
    </div>
  )
}
